import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { formatDateTime } from "./servicesUtils";

function sortNewestFirst(entries) {
  return [...(entries || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
}

function NotesGroup({ title, entries, dense }) {
  const sorted = sortNewestFirst(entries);

  return (
    <Box sx={{ "&:not(:last-of-type)": { mb: dense ? 0.75 : 1 } }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, fontSize: dense ? "0.68rem" : "0.75rem" }}
      >
        {title}
      </Typography>
      {sorted.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", fontSize: dense ? "0.68rem" : "0.75rem" }}
        >
          No notes.
        </Typography>
      ) : (
        sorted.map((entry) => (
          <Box key={entry.id} sx={{ mt: 0.4 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                whiteSpace: "pre-wrap",
                fontSize: dense ? "0.68rem" : "0.75rem",
              }}
            >
              {entry.content}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: dense ? "0.62rem" : "0.7rem" }}
            >
              {entry.created_by_name || "Unknown"} ·{" "}
              {formatDateTime(entry.created_at)}
              {entry.updated_at ? " (edited)" : ""}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

// Just the Job notes / Internal notes groups, with no toggle - reused by
// ViewNotesSection below (self-toggling) and by DetailsNotesTabs (externally
// controlled).
export function ViewNotesContent({ job, dense = false }) {
  const jobNoteEntries = job?.job_notes_entries || [];
  const internalNoteEntries = job?.internal_notes_entries || [];

  return (
    <Box
      sx={{
        p: dense ? 0.75 : 1,
        borderRadius: 1.5,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <NotesGroup title="Job notes" entries={jobNoteEntries} dense={dense} />
      <NotesGroup
        title="Internal notes"
        entries={internalNoteEntries}
        dense={dense}
      />
    </Box>
  );
}

// Read-only notes viewer for the job list card - shows Job notes and
// Internal notes side by side with the "Details" toggle. Adding/editing
// notes is only available from the job detail drawer.
export default function ViewNotesSection({ job, dense = false }) {
  const [open, setOpen] = useState(false);

  return (
    <Box>
      <Box
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.4,
          cursor: "pointer",
          userSelect: "none",
          width: "fit-content",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: dense ? "0.68rem" : "0.75rem", fontWeight: 600 }}
        >
          View Notes
        </Typography>
        <ExpandMoreOutlinedIcon
          sx={{
            fontSize: dense ? 14 : 16,
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
        />
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: dense ? 0.5 : 0.75 }}>
          <ViewNotesContent job={job} dense={dense} />
        </Box>
      </Collapse>
    </Box>
  );
}
